import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash2, Loader2, Sheet, Image as ImageIcon, Pencil, X, Settings,
  TrendingUp, Layers, Package, ListChecks, BarChart3, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { safeLS } from "@/lib/safeStorage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CULTIVOS = ["Soja", "Maíz", "Trigo"];
const CONDICIONES = ["Puesto en puerto", "En fábrica", "Retirado"];
const money = (n) => "USD " + Math.round(Number(n) || 0).toLocaleString("es-AR");
const money2 = (n) => "USD " + Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtT = (n) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (n) => `${Number(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`;

const cultKey = (c) => {
  const n = (c || "").toLowerCase();
  if (n.includes("soja") || n.includes("soya")) return "soja";
  if (n.includes("trig")) return "trigo";
  if (n.includes("ma") && n.includes("z")) return "maiz";
  return null;
};

const SEM = {
  verde: { dot: "bg-emerald-500", txt: "text-emerald-700", bg: "bg-emerald-50", label: "Ganancia" },
  amarillo: { dot: "bg-amber-400", txt: "text-amber-700", bg: "bg-amber-50", label: "Margen bajo" },
  rojo: { dot: "bg-red-500", txt: "text-red-700", bg: "bg-red-50", label: "Pérdida" },
};
function Semaforo({ s, withLabel }) {
  const d = SEM[s] || SEM.amarillo;
  return (
    <span className={`inline-flex items-center gap-1.5 ${withLabel ? "rounded-full px-2 py-0.5 " + d.bg + " " + d.txt + " text-[11px] font-bold" : ""}`} data-testid={`semaforo-${s}`}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${d.dot}`} />
      {withLabel && d.label}
    </span>
  );
}

/* Cálculo en vivo compartido con el backend */
function computeVenta({ precio, toneladas, origenes, costos, chicago, cultivo, margenMin }) {
  const tn = Number(toneladas) || 0;
  const ingreso = tn * (Number(precio) || 0);
  let costoGrano = 0;
  for (const o of origenes) {
    const otn = Number(o.toneladas) || 0;
    let costoT = Number(o.costo) || 0;
    if (!(costoT > 0)) costoT = Number(chicago?.[cultKey(o.cultivo || cultivo)]) || 0;
    costoGrano += otn * costoT;
  }
  let costosTotal = 0;
  for (const c of costos) {
    costosTotal += c.modo === "por_t" ? (Number(c.usd_t) || 0) * tn : (Number(c.monto) || 0);
  }
  const resultado = ingreso - costoGrano - costosTotal;
  const margenUsdT = tn ? resultado / tn : 0;
  const margenPct = ingreso ? (resultado / ingreso) * 100 : 0;
  const semaforo = resultado < 0 ? "rojo" : margenPct < (margenMin ?? 2) ? "amarillo" : "verde";
  return { ingreso, costoGrano, costosTotal, resultado, margenUsdT, margenPct, semaforo };
}

/* ======================= Formulario Nueva / Editar ======================= */
function VentaForm({ open, setOpen, editing, config, chicago, onSaved }) {
  const [datos, setDatos] = useState({ comprador: "", cultivo: "Soja", condicion: "Puesto en puerto", precio_venta: "", fecha: "", zafra: "", documento: "", observaciones: "" });
  const [costos, setCostos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [links, setLinks] = useState({}); // key -> { tn, costo }
  const [prodFilter, setProdFilter] = useState("");
  const [loadingC, setLoadingC] = useState(false);
  const [saving, setSaving] = useState(false);
  const origLinks = useMemo(() => {
    const m = {};
    (editing?.origenes || []).forEach((o) => {
      const k = `${o.cultivo || ""}||${o.contrato || ""}||${o.cliente || ""}`;
      m[k] = Number(o.toneladas) || 0;
    });
    return m;
  }, [editing]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setDatos({
        comprador: editing.comprador || "", cultivo: editing.cultivo || "Soja",
        condicion: editing.condicion || "Puesto en puerto", precio_venta: editing.precio_venta ?? "",
        fecha: editing.fecha || "", zafra: editing.zafra || "", documento: editing.documento || "",
        observaciones: editing.observaciones || "",
      });
      setCostos((editing.costos || []).map((c) => ({ ...c, tipoCustom: "" })));
      const lk = {};
      (editing.origenes || []).forEach((o) => {
        const k = `${o.cultivo || ""}||${o.contrato || ""}||${o.cliente || ""}`;
        lk[k] = { tn: Number(o.toneladas) || 0, costo: Number(o.costo_adquisicion ?? o.contrato_precio) || 0 };
      });
      setLinks(lk);
    } else {
      setDatos({ comprador: "", cultivo: "Soja", condicion: "Puesto en puerto", precio_venta: "", fecha: new Date().toISOString().slice(0, 10), zafra: "", documento: "", observaciones: "" });
      setCostos([]); setLinks({});
    }
    setProdFilter("");
  }, [open, editing]);

  const loadContratos = useCallback(async () => {
    if (!open || !datos.cultivo) return;
    setLoadingC(true);
    try {
      const { data } = await api.get("/granos/ventas/origen-opciones", { params: { cultivo: datos.cultivo } });
      setContratos(data.contratos || []);
    } catch {
      toast.error("No se pudieron cargar los contratos");
    } finally { setLoadingC(false); }
  }, [open, datos.cultivo]);
  useEffect(() => { loadContratos(); }, [loadContratos]);

  const maxTn = (c) => {
    const k = c.key;
    const added = origLinks[k] || 0; // devolver tn de esta misma venta al editar
    return Math.max(0, (Number(c.saldo) || 0) + added);
  };
  const setLinkTn = (c, tn) => {
    const m = maxTn(c);
    const val = Math.max(0, Math.min(m, Number(tn) || 0));
    setLinks((o) => ({ ...o, [c.key]: { tn: val, costo: o[c.key]?.costo ?? c.costo } }));
  };
  const setLinkCosto = (c, costo) => setLinks((o) => ({ ...o, [c.key]: { tn: o[c.key]?.tn ?? 0, costo: Number(costo) || 0 } }));

  const origenes = useMemo(() => contratos
    .filter((c) => (links[c.key]?.tn || 0) > 0)
    .map((c) => ({ contrato: c.contrato, cultivo: c.cultivo, cliente: c.cliente, tipo_contrato: c.tipo_contrato, toneladas: links[c.key].tn, costo: links[c.key]?.costo ?? c.costo })), [contratos, links]);

  const totalTn = useMemo(() => origenes.reduce((s, o) => s + (Number(o.toneladas) || 0), 0), [origenes]);
  const calc = useMemo(() => computeVenta({
    precio: datos.precio_venta, toneladas: totalTn, origenes, costos, chicago, cultivo: datos.cultivo, margenMin: config?.margen_min_pct,
  }), [datos.precio_venta, totalTn, origenes, costos, chicago, datos.cultivo, config]);

  const visibleContratos = contratos.filter((c) => !prodFilter.trim() || (c.cliente || "").toLowerCase().includes(prodFilter.trim().toLowerCase()));

  const addCosto = () => setCostos((o) => [...o, { tipo: (config?.costo_tipos?.[0]) || "Flete", modo: "total", monto: "", usd_t: "", proveedor: "", nota: "", tipoCustom: "" }]);
  const setCosto = (i, patch) => setCostos((o) => o.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const delCosto = (i) => setCostos((o) => o.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!datos.comprador.trim()) { toast.error("Indicá el comprador."); return; }
    if (!(Number(datos.precio_venta) > 0)) { toast.error("Indicá el precio de venta USD/t."); return; }
    if (!origenes.length) { toast.error("Vinculá al menos un contrato con toneladas."); return; }
    setSaving(true);
    try {
      const payload = {
        ...datos, precio_venta: Number(datos.precio_venta) || 0, toneladas: totalTn,
        origenes: origenes.map((o) => {
          const base = contratos.find((c) => c.contrato === o.contrato && c.cliente === o.cliente && c.cultivo === o.cultivo);
          const contratoPrecio = Number(base?.costo) || 0;
          const manual = Number(o.costo) > 0 && Number(o.costo) !== contratoPrecio;
          return { contrato: o.contrato, cultivo: o.cultivo, cliente: o.cliente, tipo_contrato: o.tipo_contrato,
                   toneladas: o.toneladas, contrato_precio: contratoPrecio, costo_adquisicion: manual ? Number(o.costo) : null };
        }),
        costos: costos.map((c) => ({ tipo: c.tipo === "__custom__" ? (c.tipoCustom || "Otros") : c.tipo, modo: c.modo,
                                     monto: Number(c.monto) || 0, usd_t: Number(c.usd_t) || 0, proveedor: c.proveedor || "", nota: c.nota || "" })),
      };
      if (editing) await api.put(`/granos/ventas/${editing.id}`, payload);
      else await api.post("/granos/ventas", payload);
      toast.success(editing ? "Venta actualizada" : "Venta registrada");
      setOpen(false); onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="venta-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#14532d]">{editing ? "Editar venta de granos" : "Nueva venta de granos"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Bloque 1 + 2 */}
          <div className="space-y-4 lg:col-span-2">
            <section className="rounded-xl border border-[#dde5dc] bg-white p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-extrabold text-[#14532d]"><FileText className="h-4 w-4" /> 1. Datos de la venta</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Field label="Comprador" className="col-span-2 sm:col-span-3">
                  <Input data-testid="venta-comprador" value={datos.comprador} onChange={(e) => setDatos({ ...datos, comprador: e.target.value })} placeholder="Nombre del comprador" />
                </Field>
                <Field label="Cultivo">
                  <Select value={datos.cultivo} onValueChange={(v) => { setDatos({ ...datos, cultivo: v }); setLinks({}); }}>
                    <SelectTrigger data-testid="venta-cultivo"><SelectValue /></SelectTrigger>
                    <SelectContent>{CULTIVOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Condición">
                  <Select value={datos.condicion} onValueChange={(v) => setDatos({ ...datos, condicion: v })}>
                    <SelectTrigger data-testid="venta-condicion"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDICIONES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Precio venta USD/t">
                  <Input data-testid="venta-precio" type="number" value={datos.precio_venta} onChange={(e) => setDatos({ ...datos, precio_venta: e.target.value })} placeholder="0" />
                </Field>
                <Field label="Fecha"><Input data-testid="venta-fecha" type="date" value={datos.fecha} onChange={(e) => setDatos({ ...datos, fecha: e.target.value })} /></Field>
                <Field label="Zafra"><Input data-testid="venta-zafra" value={datos.zafra} onChange={(e) => setDatos({ ...datos, zafra: e.target.value })} placeholder="2025/2026" /></Field>
                <Field label="Documento / factura"><Input data-testid="venta-documento" value={datos.documento} onChange={(e) => setDatos({ ...datos, documento: e.target.value })} /></Field>
                <Field label="Observaciones" className="col-span-2 sm:col-span-3"><Input data-testid="venta-obs" value={datos.observaciones} onChange={(e) => setDatos({ ...datos, observaciones: e.target.value })} /></Field>
              </div>
            </section>

            <section className="rounded-xl border border-[#dde5dc] bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-sm font-extrabold text-[#14532d]"><Layers className="h-4 w-4" /> 2. Costos de la venta</h3>
                <Button size="sm" variant="outline" onClick={addCosto} data-testid="venta-add-costo" className="h-8 gap-1"><Plus className="h-3.5 w-3.5" /> Agregar costo</Button>
              </div>
              {!costos.length && <p className="text-xs text-slate-400">Sin costos. Agregá flete, procesamiento, etc.</p>}
              <div className="space-y-2">
                {costos.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-lg bg-[#f6f9f5] p-2" data-testid={`venta-costo-row-${i}`}>
                    <div className="col-span-4">
                      <label className="text-[10px] font-semibold text-slate-500">Tipo</label>
                      <Select value={(config?.costo_tipos || []).includes(c.tipo) ? c.tipo : (c.tipo ? "__custom__" : (config?.costo_tipos?.[0] || "Flete"))}
                        onValueChange={(v) => setCosto(i, { tipo: v, tipoCustom: v === "__custom__" ? c.tipoCustom : "" })}>
                        <SelectTrigger className="h-8" data-testid={`venta-costo-tipo-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(config?.costo_tipos || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          <SelectItem value="__custom__">Otro (escribir)…</SelectItem>
                        </SelectContent>
                      </Select>
                      {((!(config?.costo_tipos || []).includes(c.tipo)) || c.tipo === "__custom__") && (
                        <Input className="mt-1 h-8" placeholder="Nombre del costo" data-testid={`venta-costo-custom-${i}`}
                          value={c.tipoCustom || (c.tipo !== "__custom__" ? c.tipo : "")} onChange={(e) => setCosto(i, { tipo: "__custom__", tipoCustom: e.target.value })} />
                      )}
                    </div>
                    <div className="col-span-3">
                      <label className="text-[10px] font-semibold text-slate-500">Modo</label>
                      <Select value={c.modo} onValueChange={(v) => setCosto(i, { modo: v })}>
                        <SelectTrigger className="h-8" data-testid={`venta-costo-modo-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="total">Total USD</SelectItem><SelectItem value="por_t">USD/t</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-semibold text-slate-500">{c.modo === "por_t" ? "USD/t" : "Monto"}</label>
                      <Input className="h-8" type="number" data-testid={`venta-costo-monto-${i}`}
                        value={c.modo === "por_t" ? c.usd_t : c.monto}
                        onChange={(e) => setCosto(i, c.modo === "por_t" ? { usd_t: e.target.value } : { monto: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] font-semibold text-slate-500">Proveedor</label>
                      <Input className="h-8" data-testid={`venta-costo-prov-${i}`} value={c.proveedor} onChange={(e) => setCosto(i, { proveedor: e.target.value })} />
                    </div>
                    <button className="col-span-1 flex h-8 items-center justify-center text-red-500 hover:text-red-700" onClick={() => delCosto(i)} data-testid={`venta-costo-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </section>

            {/* Bloque 3: contratos */}
            <section className="rounded-xl border border-[#dde5dc] bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-extrabold text-[#14532d]"><ListChecks className="h-4 w-4" /> 3. Vincular contratos de origen</h3>
                <Input className="h-8 w-48" placeholder="Filtrar por productor/cliente" value={prodFilter} onChange={(e) => setProdFilter(e.target.value)} data-testid="venta-prod-filter" />
              </div>
              {loadingC ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando contratos…</div>
                : !visibleContratos.length ? <p className="text-xs text-slate-400">No hay contratos con saldo para este cultivo.</p>
                : (
                  <div className="space-y-2">
                    {visibleContratos.map((c) => {
                      const link = links[c.key] || { tn: 0, costo: c.costo };
                      const m = maxTn(c);
                      const vendidoAntes = Number(c.vendidas) || 0;
                      const estaVenta = Number(link.tn) || 0;
                      const queda = m - estaVenta;
                      const rec = Number(c.recibidas) || 1;
                      const costoShown = (link.costo ?? c.costo) || (chicago?.[cultKey(c.cultivo)] ?? 0);
                      const estimado = !((link.costo ?? c.costo) > 0);
                      return (
                        <div key={c.key} className="rounded-lg border border-[#e2ece3] bg-[#fbfdfb] p-3" data-testid={`venta-contrato-${c.contrato}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-bold text-[#14231a]">{c.cliente}</div>
                              <div className="text-[11px] text-slate-500">Ctr {c.contrato} · {c.tipo_contrato || "—"} · saldo <b>{fmtT(m)} t</b></div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkTn(c, estaVenta - 100)} data-testid={`venta-c-minus-${c.contrato}`}>−100</Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkTn(c, estaVenta + 100)} data-testid={`venta-c-plus-${c.contrato}`}>+100</Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkTn(c, m)} data-testid={`venta-c-all-${c.contrato}`}>Todo</Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkTn(c, 0)} data-testid={`venta-c-zero-${c.contrato}`}>0</Button>
                              <Input className="h-7 w-24" type="number" value={estaVenta || ""} onChange={(e) => setLinkTn(c, e.target.value)} data-testid={`venta-c-tn-${c.contrato}`} />
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div className="bg-slate-400" style={{ width: `${(vendidoAntes / rec) * 100}%` }} title="Vendido antes" />
                              <div className="bg-emerald-500" style={{ width: `${(estaVenta / rec) * 100}%` }} title="Esta venta" />
                            </div>
                            <span className="text-[10px] text-slate-500">queda {fmtT(queda)} t</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                            <span>Costo USD/t</span>
                            <Input className="h-7 w-24" type="number" value={link.costo ?? c.costo ?? ""} onChange={(e) => setLinkCosto(c, e.target.value)} data-testid={`venta-c-costo-${c.contrato}`} />
                            {estimado && <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">estimado (Chicago {money(chicago?.[cultKey(c.cultivo)])}/t)</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </section>
          </div>

          {/* Panel resultado en vivo */}
          <aside className="space-y-3">
            <div className="sticky top-2 rounded-xl border border-[#14532d] bg-[#0f3d21] p-4 text-white" data-testid="venta-live-result">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-[#b7d9c2]">Resultado</span>
                <Semaforo s={calc.semaforo} />
              </div>
              <div className={`text-3xl font-extrabold ${calc.resultado < 0 ? "text-red-300" : "text-white"}`}>{money(calc.resultado)}</div>
              <div className="mt-1 text-xs text-[#b7d9c2]">Margen {money(calc.margenUsdT)}/t · {pct(calc.margenPct)}</div>
              <div className="mt-4 space-y-1.5 text-sm">
                <Row l="Toneladas" v={`${fmtT(totalTn)} t`} />
                <Row l="Ingreso" v={money(calc.ingreso)} />
                <Row l="− Costo del grano" v={money(calc.costoGrano)} />
                <Row l="− Costos de la venta" v={money(calc.costosTotal)} />
                <div className="my-1 border-t border-white/20" />
                <Row l="= Resultado" v={money(calc.resultado)} bold />
              </div>
            </div>
          </aside>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="venta-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="venta-save" className="gap-2 bg-[#14532d] hover:bg-[#0f3d21]">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Guardar cambios" : "Registrar venta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const Field = ({ label, className = "", children }) => (
  <div className={className}><label className="mb-1 block text-[10px] font-semibold text-slate-500">{label}</label>{children}</div>
);
const Row = ({ l, v, bold }) => (
  <div className="flex items-center justify-between"><span className="text-[#cfe3d6]">{l}</span><span className={bold ? "font-extrabold" : "font-semibold"}>{v}</span></div>
);

/* ======================= Detalle de venta (cascada) ======================= */
function VentaDetalle({ venta, onClose }) {
  if (!venta) return null;
  const steps = [
    { l: "Ingreso", v: venta.ingreso, sign: "+" },
    { l: "Costo del grano", v: -venta.costo_grano, sign: "−" },
    ...(venta.costos || []).map((c) => ({ l: c.tipo, v: -(c.monto_calc || 0), sign: "−" })),
  ];
  return (
    <Dialog open={!!venta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="venta-detalle-dialog">
        <DialogHeader><DialogTitle className="text-[#14532d]">Detalle · {venta.comprador} · {venta.cultivo}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-[#f6f9f5] p-3">
            <div><div className="text-xs text-slate-500">{venta.fecha} · {venta.condicion} · {fmtT(venta.toneladas)} t @ {money(venta.precio_venta)}/t</div></div>
            <Semaforo s={venta.semaforo} withLabel />
          </div>
          <div className="space-y-1">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center justify-between border-b border-dashed border-slate-200 py-1.5 text-sm">
                <span className="text-slate-600">{s.sign} {s.l}</span>
                <span className={`font-semibold tabular-nums ${s.v < 0 ? "text-red-600" : "text-emerald-700"}`}>{money(s.v)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-2 text-base font-extrabold">
              <span>= Resultado</span>
              <span className={venta.resultado < 0 ? "text-red-600" : "text-emerald-700"}>{money(venta.resultado)}</span>
            </div>
            <div className="text-xs text-slate-500">Margen {money(venta.margen_usd_t)}/t · {pct(venta.margen_pct)}</div>
          </div>
          <div>
            <h4 className="mb-1 text-sm font-bold text-[#14532d]">Grano de estos contratos</h4>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-[#f6f9f5] text-left text-xs text-slate-500"><tr><th className="px-3 py-1.5">Cliente</th><th className="px-3 py-1.5">Contrato</th><th className="px-3 py-1.5 text-right">Tn</th><th className="px-3 py-1.5 text-right">Costo USD/t</th><th className="px-3 py-1.5 text-right">Subtotal</th></tr></thead>
                <tbody>
                  {(venta.origenes || []).map((o, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{o.cliente}</td>
                      <td className="px-3 py-1.5">{o.contrato} <span className="text-[10px] text-slate-400">{o.tipo_contrato}</span></td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtT(o.toneladas)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(o.costo_adquisicion)}{o.estimado && <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-700">est</span>}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(o.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ======================= Config (admin) ======================= */
function ConfigDialog({ open, setOpen, config, onSaved }) {
  const [tipos, setTipos] = useState([]);
  const [margen, setMargen] = useState(2);
  useEffect(() => { if (open) { setTipos(config?.costo_tipos || []); setMargen(config?.margen_min_pct ?? 2); } }, [open, config]);
  const save = async () => {
    try {
      await api.put("/granos/ventas/config", { costo_tipos: tipos.filter((t) => t.trim()), margen_min_pct: Number(margen) || 0 });
      toast.success("Configuración guardada"); setOpen(false); onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="venta-config-dialog">
        <DialogHeader><DialogTitle className="text-[#14532d]">Configuración · Ventas de granos</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Margen mínimo (%) para semáforo amarillo"><Input type="number" value={margen} onChange={(e) => setMargen(e.target.value)} data-testid="config-margen" /></Field>
          <div>
            <div className="mb-1 flex items-center justify-between"><label className="text-xs font-semibold text-slate-500">Tipos de costo</label>
              <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setTipos((o) => [...o, ""])} data-testid="config-add-tipo"><Plus className="h-3 w-3" /> Agregar</Button></div>
            <div className="space-y-1.5">
              {tipos.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={t} onChange={(e) => setTipos((o) => o.map((x, idx) => (idx === i ? e.target.value : x)))} data-testid={`config-tipo-${i}`} />
                  <button className="text-red-500" onClick={() => setTipos((o) => o.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#14532d] hover:bg-[#0f3d21]" data-testid="config-save">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ======================= Principal ======================= */
export default function GranosVentas({ user, canAdmin }) {
  const [tab, setTab] = useState("resumen");
  const [data, setData] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [stock, setStock] = useState([]);
  const [porContrato, setPorContrato] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ cultivo: "__all__", zafra: "__all__", comprador: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [delId, setDelId] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (f.cultivo !== "__all__") p.cultivo = f.cultivo;
    if (f.zafra !== "__all__") p.zafra = f.zafra;
    if (f.comprador.trim()) p.comprador = f.comprador.trim();
    return p;
  }, [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, res, stk, ctr] = await Promise.all([
        api.get("/granos/ventas", { params }),
        api.get("/granos/ventas/resumen", { params }),
        api.get("/granos/ventas/stock", { params: f.cultivo !== "__all__" ? { cultivo: f.cultivo } : {} }),
        api.get("/granos/ventas/contratos-resultado", { params: f.cultivo !== "__all__" ? { cultivo: f.cultivo } : {} }),
      ]);
      setData(lst.data); setResumen(res.data); setStock(stk.data.stock || []); setPorContrato(ctr.data.contratos || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron cargar las ventas");
    } finally { setLoading(false); }
  }, [params, f.cultivo]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportar = (formato) => {
    const q = new URLSearchParams({ formato, ...params });
    const token = safeLS.getItem("tm_token");
    fetch(`${API}/granos/ventas/export?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = formato === "image" ? "Ventas_granos.png" : "Ventas_granos.xlsx"; a.click(); URL.revokeObjectURL(url);
      }).catch(() => toast.error("No se pudo exportar"));
  };

  const openNueva = () => { setEditing(null); setFormOpen(true); };
  const openEdit = async (v) => { try { const { data } = await api.get(`/granos/ventas/${v.id}`); setEditing(data); setFormOpen(true); } catch { toast.error("No se pudo abrir"); } };
  const anular = async () => {
    try { await api.delete(`/granos/ventas/${delId}`); toast.success("Venta anulada · toneladas devueltas al saldo"); setDelId(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo anular"); }
  };

  const ventas = data?.ventas || [];
  const config = data?.config;
  const chicago = data?.chicago || {};

  const TABS = [
    { k: "resumen", label: "Resumen", Icon: BarChart3 },
    { k: "ventas", label: "Ventas", Icon: TrendingUp },
    { k: "contratos", label: "Por contrato", Icon: ListChecks },
    { k: "stock", label: "Stock por vender", Icon: Package },
  ];

  const porCliente = useMemo(() => {
    const m = new Map();
    for (const c of porContrato) {
      const o = m.get(c.cliente) || { cliente: c.cliente, recibidas: 0, vendidas: 0, saldo: 0, resultado_acum: 0, contratos: [] };
      o.recibidas += c.recibidas || 0; o.vendidas += c.vendidas || 0; o.saldo += c.saldo || 0; o.resultado_acum += c.resultado_acum || 0;
      o.contratos.push(c); m.set(c.cliente, o);
    }
    return [...m.values()].sort((a, b) => b.resultado_acum - a.resultado_acum);
  }, [porContrato]);

  return (
    <div data-testid="granos-ventas" className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={openNueva} data-testid="venta-nueva-btn" className="gap-2 bg-[#14532d] hover:bg-[#0f3d21]"><Plus className="h-4 w-4" /> Nueva venta</Button>
        <Button variant="outline" onClick={() => exportar("excel")} className="gap-2" data-testid="venta-export-excel"><Sheet className="h-4 w-4 text-emerald-600" /> Excel</Button>
        <Button variant="outline" onClick={() => exportar("image")} className="gap-2" data-testid="venta-export-image"><ImageIcon className="h-4 w-4 text-sky-600" /> Imagen</Button>
        {canAdmin && <Button variant="outline" onClick={() => setCfgOpen(true)} className="gap-2" data-testid="venta-config-btn"><Settings className="h-4 w-4" /> Ajustes</Button>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={f.cultivo} onValueChange={(v) => setF({ ...f, cultivo: v })}>
            <SelectTrigger className="h-9 w-32 bg-white" data-testid="venta-filter-cultivo"><SelectValue placeholder="Cultivo" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">Todos los cultivos</SelectItem>{(data?.cultivos || CULTIVOS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.zafra} onValueChange={(v) => setF({ ...f, zafra: v })}>
            <SelectTrigger className="h-9 w-32 bg-white" data-testid="venta-filter-zafra"><SelectValue placeholder="Zafra" /></SelectTrigger>
            <SelectContent><SelectItem value="__all__">Todas</SelectItem>{(data?.zafras || []).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="h-9 w-40 bg-white" placeholder="Comprador" value={f.comprador} onChange={(e) => setF({ ...f, comprador: e.target.value })} data-testid="venta-filter-comprador" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#dde5dc]">
        {TABS.map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`venta-tab-${k}`}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === k ? "border-[#14532d] text-[#14532d]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex items-center gap-2 py-10 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div> : (
        <>
          {tab === "resumen" && resumen && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="venta-resumen-cards">
              <Card label="Resultado total" value={money(resumen.resultado_total)} accent={resumen.resultado_total < 0 ? "text-red-600" : "text-emerald-700"} />
              <Card label="Toneladas vendidas" value={`${fmtT(resumen.tn_vendidas)} t`} />
              <Card label="Margen promedio" value={`${money(resumen.margen_prom_usd_t)}/t`} sub={pct(resumen.margen_pct)} />
              <Card label="Stock por vender" value={`${fmtT(resumen.stock_por_vender_tn)} t`} />
            </div>
          )}

          {tab === "ventas" && (
            <div className="overflow-x-auto rounded-xl border border-[#dde5dc] bg-white">
              <table className="w-full text-sm" data-testid="venta-tabla">
                <thead className="bg-[#f6f9f5] text-left text-xs text-slate-500">
                  <tr><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Comprador</th><th className="px-3 py-2">Cultivo</th><th className="px-3 py-2 text-right">Tn</th><th className="px-3 py-2 text-right">Precio</th><th className="px-3 py-2 text-right">Costos</th><th className="px-3 py-2 text-right">Resultado</th><th className="px-3 py-2 text-center">Estado</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody>
                  {!ventas.length && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Sin ventas registradas. Creá la primera con “Nueva venta”.</td></tr>}
                  {ventas.map((v) => (
                    <tr key={v.id} className="border-t border-slate-100 hover:bg-[#fafcf9]">
                      <td className="px-3 py-2 whitespace-nowrap">{v.fecha}</td>
                      <td className="px-3 py-2 font-semibold">{v.comprador}</td>
                      <td className="px-3 py-2">{v.cultivo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtT(v.toneladas)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(v.precio_venta)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(v.costos_total)}</td>
                      <td className={`px-3 py-2 text-right font-bold tabular-nums ${v.resultado < 0 ? "text-red-600" : "text-emerald-700"}`}>{money(v.resultado)}</td>
                      <td className="px-3 py-2 text-center"><Semaforo s={v.semaforo} /></td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setDetalle(v)} className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Detalle" data-testid={`venta-detalle-${v.id}`}><FileText className="h-4 w-4" /></button>
                          <button onClick={() => openEdit(v)} className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Editar" data-testid={`venta-edit-${v.id}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => setDelId(v.id)} className="rounded p-1 text-red-500 hover:bg-red-50" title="Anular" data-testid={`venta-del-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "contratos" && (
            <div className="space-y-3" data-testid="venta-por-contrato">
              {!porCliente.length && <p className="text-sm text-slate-400">Sin datos.</p>}
              {porCliente.map((cl) => (
                <div key={cl.cliente} className="rounded-xl border border-[#dde5dc] bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-extrabold text-[#14231a]">{cl.cliente}</div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Recibidas <b>{fmtT(cl.recibidas)}</b></span>
                      <span>Vendidas <b>{fmtT(cl.vendidas)}</b></span>
                      <span>Saldo <b>{fmtT(cl.saldo)}</b></span>
                      <span className={`font-bold ${cl.resultado_acum < 0 ? "text-red-600" : "text-emerald-700"}`}>Resultado {money(cl.resultado_acum)}</span>
                    </div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {cl.contratos.map((c) => (
                      <div key={c.key} className="rounded-lg bg-[#f6f9f5] p-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold">Ctr {c.contrato} · {c.tipo_contrato || "—"}</span>
                          <span className="text-slate-500">saldo {fmtT(c.saldo)} / {fmtT(c.recibidas)} t · <b className={c.resultado_acum < 0 ? "text-red-600" : "text-emerald-700"}>{money(c.resultado_acum)}</b></span>
                        </div>
                        {!!(c.ventas || []).length && (
                          <div className="mt-1 divide-y divide-slate-200/70 text-[11px]">
                            {c.ventas.map((vn, i) => (
                              <div key={i} className="flex items-center justify-between py-1 text-slate-600">
                                <span>{vn.fecha} · {vn.comprador}</span>
                                <span>{fmtT(vn.toneladas)} t · <b className={vn.resultado < 0 ? "text-red-600" : "text-emerald-700"}>{money(vn.resultado)}</b></span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "stock" && (
            <div className="overflow-x-auto rounded-xl border border-[#dde5dc] bg-white">
              <table className="w-full text-sm" data-testid="venta-stock-tabla">
                <thead className="bg-[#f6f9f5] text-left text-xs text-slate-500">
                  <tr><th className="px-3 py-2">Cultivo</th><th className="px-3 py-2 text-right">Recibidas</th><th className="px-3 py-2 text-right">Vendidas</th><th className="px-3 py-2 text-right">Stock</th><th className="px-3 py-2 text-right">Costo prom.</th><th className="px-3 py-2 text-right">Chicago USD/t</th><th className="px-3 py-2 text-right">Resultado estimado</th></tr>
                </thead>
                <tbody>
                  {!stock.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">Sin stock.</td></tr>}
                  {stock.map((s) => (
                    <tr key={s.cultivo} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold">{s.cultivo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtT(s.recibidas)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtT(s.vendidas)}</td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">{fmtT(s.stock)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.costo_promedio != null ? money(s.costo_promedio) : "—"}{s.costo_estimado && <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-700">est</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.chicago_usd_t != null ? money(s.chicago_usd_t) : "—"}</td>
                      <td className={`px-3 py-2 text-right font-bold tabular-nums ${(s.resultado_estimado || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{s.resultado_estimado != null ? money(s.resultado_estimado) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <VentaForm open={formOpen} setOpen={setFormOpen} editing={editing} config={config} chicago={chicago} onSaved={load} />
      <VentaDetalle venta={detalle} onClose={() => setDetalle(null)} />
      {canAdmin && <ConfigDialog open={cfgOpen} setOpen={setCfgOpen} config={config} onSaved={load} />}
      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent data-testid="venta-del-dialog">
          <AlertDialogHeader><AlertDialogTitle>Anular venta</AlertDialogTitle>
            <AlertDialogDescription>Se devolverán las toneladas al saldo de los contratos. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={anular} className="bg-red-600 hover:bg-red-700" data-testid="venta-del-confirm">Anular</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Card = ({ label, value, sub, accent }) => (
  <div className="rounded-xl border border-[#dde5dc] bg-white p-4">
    <div className="text-xs font-semibold text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-extrabold ${accent || "text-[#14231a]"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400">{sub}</div>}
  </div>
);
